// 设置面板：语言与关于（仅用于顶栏右侧 Sheet，无独立设置页）

import { useState } from "react";
import { Globe, Check, Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { showSuccess } from "@/lib/toast";
import { cn } from "@/lib/utils";

const LANGUAGES = [
  { id: "en", name: "English", flag: "🇺🇸" },
  { id: "zh-CN", name: "简体中文", flag: "🇨🇳" },
  { id: "zh-TW", name: "繁體中文", flag: "🇭🇰" },
  { id: "vi", name: "Tiếng Việt", flag: "🇻🇳" },
  { id: "th", name: "ไทย", flag: "🇹🇭" },
  { id: "pt", name: "Português", flag: "🇵🇹" },
  { id: "ko", name: "한국어", flag: "🇰🇷" },
  { id: "ja", name: "日本語", flag: "🇯🇵" },
  { id: "id", name: "Bahasa Indonesia", flag: "🇮🇩" },
  { id: "hi", name: "हिन्दी", flag: "🇮🇳" },
];

function i18nLngToUiId(lng: string): string {
  if (lng === "zh") return "zh-CN";
  if (LANGUAGES.some(l => l.id === lng)) return lng;
  return "zh-CN";
}

const APP_VERSION = "1.2.0";

export function SettingsPanel() {
  const { t } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    try {
      const saved = localStorage.getItem("language");
      if (saved && LANGUAGES.some(l => l.id === saved)) return saved;
    } catch {
      /* ignore */
    }
    return i18nLngToUiId(i18n.language);
  });

  const handleLanguageChange = (langId: string) => {
    i18n.changeLanguage(langId);
    setCurrentLanguage(langId);
    localStorage.setItem("language", langId);
    const langName = LANGUAGES.find(l => l.id === langId)?.name ?? "";
    showSuccess(t("setting_page_toast_language_changed", { name: langName }));
  };

  const headerBlock = (
    <div
      className="shrink-0 px-4 pt-5 pb-4"
      style={{
        background:
          "linear-gradient(to bottom, oklch(0.15 0.015 260), oklch(0.13 0.01 260))",
        borderBottom: "1px solid oklch(1 0 0 / 6%)",
      }}
    >
      <h1
        className="text-xl font-bold"
        style={{ fontFamily: "Sora, sans-serif" }}
      >
        {t("setting_page_title")}
      </h1>
      <p className="text-xs mt-1" style={{ color: "oklch(0.40 0.008 265)" }}>
        {t("setting_page_subtitle")}
      </p>
    </div>
  );

  const mainBlock = (
    <div
      className={cn(
        "px-4 py-4 space-y-4",
        "flex-1 min-h-0 overflow-y-auto overscroll-contain pb-8"
      )}
    >
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Globe size={16} style={{ color: "oklch(0.85 0.22 145)" }} />
          <h2 className="font-semibold text-sm">
            {t("setting_page_language_heading")}
          </h2>
        </div>

        <div className="space-y-2">
          {LANGUAGES.map(lang => (
            <button
              key={lang.id}
              type="button"
              onClick={() => handleLanguageChange(lang.id)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all"
              style={{
                background:
                  currentLanguage === lang.id
                    ? "oklch(0.75 0.15 75 / 15%)"
                    : "oklch(0.17 0.012 260)",
                border: `1px solid ${currentLanguage === lang.id ? "oklch(0.75 0.15 75 / 50%)" : "oklch(1 0 0 / 8%)"}`,
              }}
            >
              <div className="flex items-center gap-3">
                <span className="text-lg">{lang.flag}</span>
                <span
                  className="text-sm"
                  style={{
                    color:
                      currentLanguage === lang.id
                        ? "oklch(0.85 0.22 145)"
                        : "oklch(0.92 0.005 265)",
                  }}
                >
                  {lang.name}
                </span>
              </div>
              {currentLanguage === lang.id && (
                <Check size={16} style={{ color: "oklch(0.85 0.22 145)" }} />
              )}
            </button>
          ))}
        </div>
      </div>

      <div
        className="rounded-2xl p-4"
        style={{
          background: "oklch(0.17 0.012 260)",
          border: "1px solid oklch(1 0 0 / 8%)",
        }}
      >
        <div className="flex items-center gap-2 mb-3">
          <Info size={16} style={{ color: "oklch(0.6 0.18 220)" }} />
          <h2 className="font-semibold text-sm">
            {t("setting_page_about_heading")}
          </h2>
        </div>

        <div className="space-y-3 text-sm">
          <div className="flex justify-between items-center">
            <span style={{ color: "oklch(0.50 0.010 265)" }}>
              {t("setting_page_app_name_label")}
            </span>
            <span style={{ color: "oklch(0.92 0.005 265)" }}>
              {t("setting_page_app_name_value")}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span style={{ color: "oklch(0.50 0.010 265)" }}>
              {t("setting_page_version_label")}
            </span>
            <span
              className="mono-num"
              style={{ color: "oklch(0.85 0.22 145)" }}
            >
              v{APP_VERSION}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span style={{ color: "oklch(0.50 0.010 265)" }}>
              {t("setting_page_network_label")}
            </span>
            <span style={{ color: "oklch(0.92 0.005 265)" }}>
              {t("setting_page_network_value")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full min-h-0 bg-background">
      {headerBlock}
      {mainBlock}
    </div>
  );
}
